import type { EtapaCronograma } from "@/components/Cronograma";
import type { FilaGantt, MarcaGantt } from "@/components/Gantt";
import type { Cronograma, PredecesoraCronograma, RubroCronograma, TareaCronograma, TipoVinculo } from "./api";
import type { OpcionBuscable } from "./buscar";
import { formatFecha, hoyISO } from "./format";

/** Cronograma por ítems: textos, barras y el form de una tarea (duración, "no antes de" y predecesoras). Los días son
 * hábiles y relativos al inicio de obra; el `fin` es exclusivo. Mismos topes que `app/cronograma/reglas.py`. */

export const MAX_DURACION = 9999;
export const MAX_ESPERA = 999;

export const TIPOS_VINCULO: { valor: TipoVinculo; etiqueta: string }[] = [
  { valor: "FC", etiqueta: "Cuando termina (fin a comienzo)" },
  { valor: "CC", etiqueta: "Cuando empieza (comienzo a comienzo)" },
];

const dias = (n: number) => `${n} ${Math.abs(n) === 1 ? "día" : "días"}`;

export function nombresTareas(c: Cronograma): Map<number, string> {
  return new Map(c.rubros.flatMap((r) => r.tareas.map((t) => [t.id, `${r.nombre} › ${t.descripcion}`] as const)));
}

/** "Bases (cuando termina, +2 días)". */
export function textoPredecesora(p: PredecesoraCronograma, nombres: Map<number, string>): string {
  const cuando = p.tipo === "FC" ? "cuando termina" : "cuando empieza";
  const espera = p.espera === 0 ? "" : `, ${p.espera > 0 ? "+" : "−"}${dias(Math.abs(p.espera))}`;
  return `${nombres.get(p.predecesora_id) ?? "Ítem"} (${cuando}${espera})`;
}

/** Cuándo se hace: fechas si hay inicio de obra, días relativos si no. */
export function textoPlazo(t: { duracion: number | null; comienzo: number | null; fin: number | null; desde: string | null; hasta: string | null }): string {
  if (t.duracion === null || t.comienzo === null || t.fin === null) return "Sin duración";
  if (t.duracion === 0) return `Hito · ${t.desde ? formatFecha(t.desde) : `día ${t.comienzo}`}`;
  const cuando = t.desde && t.hasta ? `${formatFecha(t.desde)} al ${formatFecha(t.hasta)}` : `Día ${t.comienzo + 1} al ${t.fin}`;
  return `${cuando} · ${dias(t.duracion)} hábiles`;
}

export function textoRubro(r: RubroCronograma): string {
  if (r.comienzo === null || r.fin === null) return "Sin programar";
  return r.desde && r.hasta ? `${formatFecha(r.desde)} al ${formatFecha(r.hasta)}` : `Día ${r.comienzo + 1} al ${r.fin}`;
}

export function textoHolgura(t: TareaCronograma): string {
  if (t.holgura === null) return "";
  return t.critico ? "Crítico" : `Holgura ${dias(t.holgura)}`;
}

const redondear2 = (n: number) => Math.round(n * 100) / 100;

/** Las barras de las tareas programadas de un rubro sobre el largo de la obra (crítico en rojo). */
export function barrasRubro(r: RubroCronograma, duracionObra: number): EtapaCronograma[] {
  if (duracionObra <= 0) return [];
  const pct = (d: number) => redondear2((d / duracionObra) * 100);
  return r.tareas
    .filter((t) => t.comienzo !== null && t.fin !== null)
    .map((t) => ({
      id: t.id,
      nombre: t.descripcion,
      detalle: textoPlazo(t),
      izquierda: pct(t.comienzo!),
      ancho: Math.max(pct(t.fin! - t.comienzo!), 0.5), // un hito se ve como una marca
      avance: 100,
      tono: t.critico ? "peligro" : "primario",
      estado: textoHolgura(t),
    }));
}

const utc = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number);
  return Date.UTC(a!, m! - 1, d!);
};

/** Días hábiles desde `desde` (incluido) hasta `hasta` (excluido). */
function habilesEntre(desde: string, hasta: string): number {
  let n = 0;
  for (let t = utc(desde); t < utc(hasta); t += 86_400_000) {
    const d = new Date(t).getUTCDay();
    if (d !== 0 && d !== 6) n += 1;
  }
  return n;
}

/** La línea de hoy en % del largo de la obra; null sin inicio o fuera de la obra. */
export function posicionHoy(c: Cronograma, hoy: string = hoyISO()): number | null {
  if (!c.inicio || c.duracion <= 0 || hoy < c.inicio) return null;
  const d = habilesEntre(c.inicio, hoy);
  return d <= c.duracion ? redondear2((d / c.duracion) * 100) : null;
}

// ------------------------------------------------------------------ vista Gantt (toda la obra)

/** Una fila del Gantt; las de tareas llevan el ítem, para abrir su edición. */
export type FilaGanttCronograma = Omit<FilaGantt, "onElegir"> & { tarea: TareaCronograma | null };

/** Rubro (barra resumen) y debajo sus tareas programadas, en el orden del cronograma; los sin programar no van. */
export function filasGantt(c: Cronograma): FilaGanttCronograma[] {
  if (c.duracion <= 0) return [];
  const pct = (d: number) => redondear2((d / c.duracion) * 100);
  return c.rubros.flatMap((r) => {
    const tareas = r.tareas.filter((t) => t.comienzo !== null && t.fin !== null);
    if (r.comienzo === null || r.fin === null || tareas.length === 0) return [];
    const rubro: FilaGanttCronograma = {
      clave: `rubro-${r.id}`, nombre: r.nombre, detalle: textoRubro(r), nivel: 0, izquierda: pct(r.comienzo),
      ancho: Math.max(pct(r.fin - r.comienzo), 0.5), hito: false, critico: r.critico, tarea: null,
    };
    return [
      rubro,
      ...tareas.map((t): FilaGanttCronograma => ({
        clave: `tarea-${t.id}`, nombre: t.descripcion, detalle: textoPlazo(t), nivel: 1, izquierda: pct(t.comienzo!),
        ancho: Math.max(pct(t.fin! - t.comienzo!), 0.5), hito: t.duracion === 0, critico: t.critico, tarea: t,
      })),
    ];
  });
}

const MES = new Intl.DateTimeFormat("es-AR", { month: "short", year: "2-digit", timeZone: "UTC" });
const PASOS_DIAS = [5, 10, 20, 50, 100, 200, 500, 1000];
const MAX_MARCAS = 12;

/** El eje del Gantt: el primer día hábil de cada mes ("nov 26") si hay fecha de inicio (salteando meses si son
 * muchos); si no, cada tantos días hábiles ("Día 20"). */
export function marcasGantt(c: Cronograma): MarcaGantt[] {
  if (c.duracion <= 0) return [];
  const pct = (d: number) => redondear2((d / c.duracion) * 100);
  if (!c.inicio) {
    const paso = PASOS_DIAS.find((p) => c.duracion / p <= MAX_MARCAS) ?? PASOS_DIAS[PASOS_DIAS.length - 1]!;
    const marcas: MarcaGantt[] = [];
    for (let d = paso; d < c.duracion; d += paso) marcas.push({ posicion: pct(d), texto: `Día ${d}` });
    return marcas;
  }
  const inicio = new Date(utc(c.inicio));
  const meses: { fecha: string; dias: number }[] = [];
  for (let m = 1; ; m += 1) {
    const primero = new Date(Date.UTC(inicio.getUTCFullYear(), inicio.getUTCMonth() + m, 1)).toISOString().slice(0, 10);
    const d = habilesEntre(c.inicio, primero);
    if (d >= c.duracion) break;
    meses.push({ fecha: primero, dias: d });
  }
  const salto = Math.max(1, Math.ceil(meses.length / MAX_MARCAS));
  return meses.filter((_, i) => i % salto === 0).map((m) => ({ posicion: pct(m.dias), texto: MES.format(utc(m.fecha)).replace(".", "") }));
}

export type VistaCronograma = "lista" | "gantt";
const CLAVE_VISTA = "cronograma_vista";

/** La vista elegida la última vez en este navegador (de entrada, la lista). */
export function leerVista(): VistaCronograma {
  try {
    return window.localStorage.getItem(CLAVE_VISTA) === "gantt" ? "gantt" : "lista";
  } catch {
    return "lista";
  }
}

export function guardarVista(v: VistaCronograma): void {
  try {
    window.localStorage.setItem(CLAVE_VISTA, v);
  } catch {
    // storage bloqueado: la vista vuelve a la lista al recargar, nada más
  }
}

export function opcionesPredecesora(c: Cronograma, itemId: number | null): OpcionBuscable[] {
  return [...nombresTareas(c)].filter(([id]) => id !== itemId).map(([id, nombre]) => ({ valor: String(id), etiqueta: nombre }));
}

export function opcionesRubro(c: Cronograma, otros: { id: number; nombre: string }[] = []): OpcionBuscable[] {
  const vistos = new Map<number, string>();
  for (const r of [...c.rubros, ...otros]) if (!vistos.has(r.id)) vistos.set(r.id, r.nombre);
  return [...vistos].map(([id, nombre]) => ({ valor: String(id), etiqueta: nombre })).sort((a, b) => a.etiqueta.localeCompare(b.etiqueta));
}

// ------------------------------------------------------------------ form de la tarea

export interface FilaPredecesora {
  predecesoraId: string;
  tipo: TipoVinculo;
  espera: string;
}

export interface FormTarea {
  duracion: string;
  noAntesDe: string;
  predecesoras: FilaPredecesora[];
}

export interface ErroresTarea {
  duracion?: string;
  noAntesDe?: string;
  predecesoras: Record<number, { predecesoraId?: string; espera?: string }>;
}

export function formTarea(t?: TareaCronograma): FormTarea {
  if (!t) return { duracion: "", noAntesDe: "", predecesoras: [] };
  return {
    duracion: t.duracion === null ? "" : String(t.duracion),
    noAntesDe: t.no_antes_de ?? "",
    predecesoras: t.predecesoras.map((p) => ({ predecesoraId: String(p.predecesora_id), tipo: p.tipo, espera: String(p.espera) })),
  };
}

export function filaPredecesoraNueva(): FilaPredecesora {
  return { predecesoraId: "", tipo: "FC", espera: "0" };
}

const ENTERO = /^-?\d+$/;

export function validarTarea(f: FormTarea): ErroresTarea {
  const e: ErroresTarea = { predecesoras: {} };
  const d = f.duracion.trim();
  if (d && (!/^\d+$/.test(d) || Number(d) > MAX_DURACION)) e.duracion = `Días hábiles, de 0 a ${MAX_DURACION}`;
  if (f.noAntesDe && !/^\d{4}-\d{2}-\d{2}$/.test(f.noAntesDe)) e.noAntesDe = "Fecha inválida";
  const vistas = new Set<string>();
  f.predecesoras.forEach((p, i) => {
    const err: { predecesoraId?: string; espera?: string } = {};
    if (!p.predecesoraId) err.predecesoraId = "Elegí el ítem";
    else if (vistas.has(p.predecesoraId)) err.predecesoraId = "Ya está en la lista";
    vistas.add(p.predecesoraId);
    const esp = p.espera.trim();
    if (esp && (!ENTERO.test(esp) || Math.abs(Number(esp)) > MAX_ESPERA)) err.espera = `De -${MAX_ESPERA} a ${MAX_ESPERA} días`;
    if (err.predecesoraId || err.espera) e.predecesoras[i] = err;
  });
  return e;
}

export function hayErroresTarea(e: ErroresTarea): boolean {
  return Boolean(e.duracion || e.noAntesDe || Object.keys(e.predecesoras).length);
}

export function aPayloadPrograma(f: FormTarea): { duracion: number | null; no_antes_de: string | null } {
  const d = f.duracion.trim();
  return { duracion: d ? Number(d) : null, no_antes_de: f.noAntesDe || null };
}

export function aPayloadPredecesoras(f: FormTarea): { predecesoras: PredecesoraCronograma[] } {
  return {
    predecesoras: f.predecesoras.map((p) => ({ predecesora_id: Number(p.predecesoraId), tipo: p.tipo, espera: p.espera.trim() ? Number(p.espera) : 0 })),
  };
}
